import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Paperclip, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getStatusStyle, getStatusLabel } from '../lib/proposalUtils';
import FileUpload from './FileUpload';
import MapButtons from './MapButtons';

export default function ProposalPreviewDialog({ open, onOpenChange, proposal, onRefresh }) {
  if (!proposal) return null;

  const addressParts = [proposal.client_city, proposal.client_state, proposal.client_zip].filter(Boolean);
  const addressLine = addressParts.join(', ');
  
  // Service address (work location)
  const serviceAddressParts = [proposal.service_address_city, proposal.service_address_state, proposal.service_address_zip].filter(Boolean);
  const serviceAddressLine = serviceAddressParts.join(', ');
  const hasServiceAddress = proposal.service_address_street || proposal.service_address_city;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview</DialogTitle>
        </DialogHeader>
        <div className="pt-4 space-y-4">
          <div className="text-center pb-4 border-b-2 border-amber-500">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Center Line Locating, LLC</h1>
            <p className="text-slate-500 text-sm sm:text-base">Professional Utility Locating Services</p>
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900">{proposal.proposal_type} Proposal</h2>
            <p className="text-slate-500">#{proposal.proposal_number}</p>
            {proposal.job_title && (
              <p className="text-slate-700 font-medium mt-1">{proposal.job_title}</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500 mb-2">Client</p>
              <p className="font-semibold break-words">{proposal.client_name}</p>
              {proposal.client_street && <p className="text-slate-600 break-words">{proposal.client_street}</p>}
              {addressLine && <p className="text-slate-600">{addressLine}</p>}
              {proposal.client_phone && <p className="text-slate-600">{proposal.client_phone}</p>}
              {proposal.client_email && <p className="text-slate-600 break-all">{proposal.client_email}</p>}
            </div>
            <div className="sm:text-right">
              <p className="text-xs font-bold uppercase text-slate-500 mb-2">Details</p>
              <p className="text-slate-600">Date: {format(new Date(proposal.created_at), 'MMM d, yyyy')}</p>
              {proposal.valid_until && <p className="text-slate-600">Valid: {format(new Date(proposal.valid_until), 'MMM d, yyyy')}</p>}
              <span className={'inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ' + getStatusStyle(proposal.status)}>{getStatusLabel(proposal.status)}</span>
            </div>
          </div>
          
          {/* Service Address (Work Location) */}
          {hasServiceAddress && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-green-600" />
                <p className="text-xs font-bold uppercase text-green-700">Service Address (Work Location)</p>
              </div>
              {proposal.service_address_street && <p className="font-medium text-slate-800">{proposal.service_address_street}</p>}
              {serviceAddressLine && <p className="text-slate-600">{serviceAddressLine}</p>}
              <div className="mt-2">
                <MapButtons 
                  address={[proposal.service_address_street, proposal.service_address_city, proposal.service_address_state, proposal.service_address_zip].filter(Boolean).join(', ')}
                  size="sm"
                  showLabels={false}
                />
              </div>
            </div>
          )}
          
          {/* Scoped Items */}
          {proposal.scopes && proposal.scopes.length > 0 && (
            <div className="space-y-4">
              {proposal.scopes.map((scope, si) => {
                const scopeTotal = (scope.items || []).reduce((s, i) => s + (i.total || 0), 0);
                return (
                  <div key={si} data-testid={`preview-scope-${si}`}>
                    <h3 className="font-bold text-slate-800 text-base mb-2 border-b pb-1">{scope.name || `Scope ${si + 1}`}</h3>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Service</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-center">Type</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Qty</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Price</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(scope.items || []).map((item, i) => {
                            const serviceName = item.service || item.description || '—';
                            const descriptionText = item.custom_description || (item.service ? item.description : '') || '—';
                            return (
                              <TableRow key={i}>
                                <TableCell className="font-medium">{serviceName}</TableCell>
                                <TableCell className="text-slate-600">{descriptionText}</TableCell>
                                <TableCell className="text-center text-sm text-slate-500">{item.rate_type === 'hour' ? '/hr' : item.rate_type === 'day' ? '/day' : item.rate_type === 'night' ? '/night' : item.rate_type === 'hole' ? '/hole' : item.rate_type === 'linear_footage' ? '/LF' : 'fixed'}</TableCell>
                                <TableCell className="text-right">{item.quantity}</TableCell>
                                <TableCell className="text-right whitespace-nowrap">${item.unit_price?.toFixed(2) || '0.00'}</TableCell>
                                <TableCell className="text-right font-medium whitespace-nowrap">${item.total?.toFixed(2) || '0.00'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="flex justify-end text-sm font-semibold text-slate-700 mt-1">
                      <span className="mr-4">Total:</span>
                      <span>${scopeTotal.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Standalone Items */}
          {(proposal.items && proposal.items.length > 0) && (
            <>
              {proposal.scopes && proposal.scopes.length > 0 && (
                <h3 className="font-bold text-slate-800 text-base border-b pb-1">Additional Items</h3>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-center">Type</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Qty</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Price</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {proposal.items.map((item, i) => {
                      const serviceName = item.service || item.description || '—';
                      const descriptionText = item.custom_description || (item.service ? item.description : '') || '—';
                      return (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{serviceName}</TableCell>
                          <TableCell className="text-slate-600">{descriptionText}</TableCell>
                          <TableCell className="text-center text-sm text-slate-500">{item.rate_type === 'hour' ? '/hr' : item.rate_type === 'day' ? '/day' : item.rate_type === 'night' ? '/night' : item.rate_type === 'hole' ? '/hole' : 'fixed'}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">${item.unit_price?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell className="text-right font-medium whitespace-nowrap">${item.total?.toFixed(2) || '0.00'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          <div className="ml-auto w-full sm:w-64 space-y-2">
            <div className="flex justify-between font-bold border-t-2 border-slate-900 pt-2"><span>Total</span><span>${proposal.total.toFixed(2)}</span></div>
          </div>
          {proposal.notes && (
            <div className="bg-slate-50 rounded-lg p-3 sm:p-4">
              <p className="font-semibold mb-2">Notes</p>
              <p className="text-slate-600 whitespace-pre-wrap">{proposal.notes}</p>
            </div>
          )}
          
          {/* Attachments Section */}
          <FileUpload
            files={proposal.attachments || []}
            uploadEndpoint={`/api/proposals/${proposal.id}/files`}
            deleteEndpoint={`/api/proposals/${proposal.id}/files`}
            onUploadSuccess={onRefresh}
            onDeleteSuccess={onRefresh}
            title="Attachments"
            emptyMessage="No files attached. Upload documents, site plans, or other relevant files."
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
